import { useRef } from 'react';

import { NextPageWithLayout } from 'next';
import { ImageLoaderProps } from 'next/image';
import get from 'ts-get';

import { useRutilusQuery } from '../../api/graphql/useRutilusQuery';
import { Avatar, AvatarSize } from '../../components/Avatar/Avatar';
import { Breadcrumbs } from '../../components/Breadcrumbs/Breadcrumbs';
import { GQLFollowButton } from '../../components/Clickables/Buttons/GQLFollowButton';
import { ExternalLink } from '../../components/Clickables/ExternalLink/ExternalLink';
import { Container } from '../../components/Container/Container';
import { Heading } from '../../components/Heading/Heading';
import { MetaTags } from '../../components/MetaTags/MetaTags';
import { MAIN_NAVIGATION_MIN_HEIGHT } from '../../components/Navigation/MainNavigation/constants/constants';
import { NextImageWrapper } from '../../components/NextImage/NextImageWrapper';
import { Text } from '../../components/Text/Text';
import { getNodesFromConnection } from '../../helpers/graphql';
import { abbreviateNumber } from '../../helpers/numbers/abbreviateNumber';
import { getPageUrl } from '../../helpers/urls';
import {
  FollowRecordsTypeEnum,
  GetPage,
  GetPageCatches,
  GetPageCatchesVariables,
  GetPageCatches_entry_catches_edges_node,
} from '../../interfaces/graphql';
import { getPageCatches } from '../../queries/rutilus/Page';
import { NotFound } from '../NotFound/NotFound';

import { PostsViewer } from '../../components/PostsViewer/PostsViewer';
import { usePostViewer } from '../../helpers/hooks/usePostViewer';
import { PageCatchesGrid } from './components/PageCatchesGrid';
import { PageFeed } from './components/PageFeed';
import { PromotedCallToAction } from './components/PromotedCallToAction';
import styles from './Page.module.css';
import { getImgProxyLoader } from '../../helpers/imageLoaders/imgProxy';

export interface IPageProps {
  pageExternalId: string;
  data: GetPage;
}

export const Page: NextPageWithLayout<IPageProps> = ({ pageExternalId, data }) => {
  const postsReference = useRef<HTMLDivElement>(null);
  const { data: catchesData, loading } = useRutilusQuery<GetPageCatches, GetPageCatchesVariables>(
    getPageCatches,
    {
      variables: { id: data.entry._id },
    },
  );
  const catchPosts = getNodesFromConnection<GetPageCatches_entry_catches_edges_node>(
    catchesData?.entry.catches,
  ).map(_catch => _catch.post);
  const fetchNextPage = (): void => {};
  const {
    currentPostViewerId,
    closePostViewer,
    isPostViewerOpen,
    openPostViewer,
    setCurrentPostViewerId,
  } = usePostViewer(postsReference, MAIN_NAVIGATION_MIN_HEIGHT + 4);

  if (!data.entry.published) {
    return <NotFound />;
  }

  const {
    displayName,
    logo,
    coverImages,
    followedByCurrentUser,
    followers,
    description,
    promotedCallToActions,
    pageable,
    _id,
  } = data.entry;

  const promotedCallToAction = getNodesFromConnection(promotedCallToActions)[0];
  const url = get(pageable, p => (p as any).url);
  const coverImage = getNodesFromConnection(coverImages)[0];
  const coverImageUrl = coverImage?.url || '';
  const coverImageWidth = coverImage?.width || 0;
  const coverImageHeight = coverImage?.height;

  const imgProxyLoader = ({ src, width, quality }: ImageLoaderProps): string =>
    getImgProxyLoader(src, width, coverImageWidth, coverImageHeight, { quality });

  const breadcrumbs = [
    {
      label: 'Brands',
      link: '/brands',
    },
    {
      label: displayName,
      link: getPageUrl(pageExternalId, displayName),
    },
  ];

  return (
    <Container>
      <div ref={postsReference}>
        <MetaTags
          title={`Follow ${displayName} on Fishbrain`}
          description={`Check out ${displayName} to see their latest updates, sale announcements, and more on Fishbrain. Follow ${displayName} to stay updated.`}
          link={getPageUrl(pageExternalId, displayName)}
          image={coverImageUrl}
        />
        <Breadcrumbs items={breadcrumbs} />
        <div className="relative mt-4 h-48 w-full lg:h-96">
          {coverImageUrl && (
            <NextImageWrapper
              alt="recently logged catches"
              wrapperClassName="h-full"
              imageClassName="h-full w-full object-cover"
              src={coverImageUrl}
              loader={imgProxyLoader}
              fill
              priority
              sizes="100vw"
            />
          )}
        </div>

        <div className={`grid text-center md:text-left ${styles.header}`}>
          <div className="relative -top-8 self-start justify-self-center">
            {logo && logo.url && (
              <Avatar
                id="brand-logo"
                alt={displayName}
                src={logo.url}
                size={AvatarSize.LARGE}
                // Some avatars use transparent backgrounds, which make them hard to see when overlayed
                // on the cover image.
                style={{ backgroundColor: 'white' }}
              />
            )}
          </div>
          <div>
            <Heading as="h1" variant="lg" className="mt-4 mb-2">
              {displayName}
            </Heading>
            <Text>{description}</Text>
            <div className="mb-2 flex flex-col justify-center lg:flex-row lg:justify-start">
              <span>{`${abbreviateNumber(followers.totalCount)} followers`}</span>
              {url && (
                <div className="mt-4 ml-0 lg:mt-0 lg:ml-6">
                  <ExternalLink href={url}>{url}</ExternalLink>
                </div>
              )}
            </div>
          </div>
          <div className="place-self-center px-4">
            <GQLFollowButton
              source="brand-page-follow-button"
              id={_id}
              type={FollowRecordsTypeEnum.PAGE}
              isFollowed={followedByCurrentUser}
              buttonProps={{ style: { width: 160 } }}
              customAppDownloadHeader={`Download the Fishbrain app to follow ${displayName}`}
            />
          </div>
        </div>
        <div className="mt-4 grid grid-cols-1 gap-4">
          {promotedCallToAction && (
            <PromotedCallToAction promotedCallToAction={promotedCallToAction} />
          )}
          {/* Catches */}
          <PageCatchesGrid
            isLoading={loading}
            catchPostList={catchPosts}
            openPostViewer={openPostViewer}
          />
          {/* Posts */}
          <PageFeed pageName={displayName} pageId={pageExternalId} />
        </div>
        {catchPosts.length > 0 && isPostViewerOpen && currentPostViewerId && (
          <PostsViewer
            posts={catchPosts}
            currentPostViewerId={currentPostViewerId}
            hasMore={false}
            isPostViewerOpen
            loadMore={fetchNextPage}
            onClosePostsViewer={closePostViewer}
            setCurrentPostViewerId={setCurrentPostViewerId}
          />
        )}
      </div>
    </Container>
  );
};
